import React, { useEffect, useRef, useState } from 'react';
import { useIsMobile } from '../../lib/hooks';
import { UniversaldiktatModal } from './UniversaldiktatModal';
import type { DiktatKind, DiktatModul } from '../../lib/diktat-api';
import { mikrofonAnfragen } from '../../lib/mikrofon';

// NavItem-Routen, auf denen der FAB nicht sichtbar sein soll
// Welle B2: auch vom schwebenden Mikro-Knopf (App.tsx FAB) genutzt — dort kein Knopf, solange der
// Bildschirm keinen eigenen Mikro-Weg (fabHandler) hat
// Welle K3: Mein Tag hat den Knopf wieder (der Kalender hat kein eigenes Mikro mehr)
export const DIKTAT_AUS_ROUTEN: ReadonlySet<string> = new Set(['einstellungen', 'datenschutz', 'schulrecht']);
const HIDE_ROUTES = DIKTAT_AUS_ROUTEN;

interface Props {
  aktiv: string;
}

export function UniversaldiktatFAB({ aktiv }: Props) {
  const [offen, setOffen] = useState(false);
  const [modul, setModul] = useState<DiktatModul | undefined>(undefined); // Welle U6b: Startmodus
  const [kind, setKind] = useState<DiktatKind | null>(null);              // Welle P1b: „Über … diktieren“
  const streamRef = useRef<Promise<MediaStream> | null>(null);

  const sichtbar = !HIDE_ROUTES.has(aktiv);
  const isMobile = useIsMobile();

  function diktatOeffnen() {
    // Pre-acquire stream in gesture context for iOS Safari
    streamRef.current = mikrofonAnfragen(); // Welle H6: ohne unbehandelte Ablehnung
    setModul(undefined);
    setOffen(true);
  }

  function diktatSchliessen() {
    streamRef.current = null;
    setOffen(false);
    setModul(undefined);
    setKind(null);
  }

  // ld-diktat (mobiler Mikro-Knopf ohne Screen-Handler): nur am Handy. Am PC gehört ld-diktat
  // (Strg+D) der Seitenleiste mit ihrer Auswahl — sonst öffneten sich beide übereinander (D3c-Befund).
  // ld-universaldiktat (Widget „Schnell-Diktat“ auf Heute): auf jedem Gerät.
  useEffect(() => {
    function handler() {
      if (!sichtbar) return;
      setKind(null);
      diktatOeffnen();
    }
    function nurMobil() { if (isMobile) handler(); }
    // Welle P1b: detail.kind = Diktat aus dem Schülerprofil (auch dort, wo der FAB sonst verborgen ist)
    // Welle K1: detail.seitenleiste = „Erzählen“ am PC — öffnet auf jedem Bildschirm
    function universal(e: Event) {
      const d = (e as CustomEvent<{ kind?: DiktatKind; seitenleiste?: boolean }>).detail;
      const k = d?.kind ?? null;
      if (!k && !sichtbar && !d?.seitenleiste) return;
      diktatOeffnen();
      setKind(k);
    }
    window.addEventListener('ld-diktat', nurMobil);
    window.addEventListener('ld-universaldiktat', universal);
    return () => { window.removeEventListener('ld-diktat', nurMobil); window.removeEventListener('ld-universaldiktat', universal); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sichtbar, isMobile]);

  // Welle U6b: Schnell-Diktat (Heute) öffnet direkt im Modus. Bewusst OHNE vorab geholten
  // Mikrofon-Stream — das Mikrofon geht erst mit dem Tipp auf den Aufnahme-Knopf an.
  useEffect(() => {
    function handler(e: Event) {
      const m = (e as CustomEvent<{ modul: DiktatModul }>).detail?.modul;
      if (!m) return;
      streamRef.current = null;
      setKind(null);
      setModul(m);
      setOffen(true);
    }
    window.addEventListener('ld-diktat-modul', handler);
    return () => window.removeEventListener('ld-diktat-modul', handler);
  }, []);

  return (
    <>
      {/* Welle D3b: kein eigener schwebender Knopf mehr am PC — Diktat startet dort über die
          Seitenleiste, das Diktat-Widget auf „Heute“ oder Strg+D (alle über das ld-diktat-Event). */}
      {/* Modal (gilt für mobile UND desktop) */}
      <UniversaldiktatModal
        open={offen}
        onClose={diktatSchliessen}
        streamPromise={streamRef.current}
        fixedModul={modul}
        kind={kind}
      />
    </>
  );
}
